/**
 * Inline back-in-stock capture for out-of-stock product cards.
 *
 * "Notify me" expands into an email field; Join stays disabled until the
 * address looks valid; success confirms in place. No modal, no page change.
 *
 * ---------------------------------------------------------------------------
 * WIRING THIS TO A BACK-IN-STOCK APP
 *
 * Only `submitSignup` below talks to a backend. Replace its body when a
 * provider is chosen and nothing else needs to change.
 *
 * Until then it posts to Shopify's own customer endpoint, which needs no app:
 * the address becomes a customer tagged `back-in-stock` plus the product
 * handle, so the waitlist is exportable and segmentable from Shopify admin.
 *
 * Klaviyo, for reference, would be a POST to their client API with the
 * product's variant id — see their back-in-stock docs for the current shape.
 * ---------------------------------------------------------------------------
 */

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Records a back-in-stock signup.
 *
 * @param {{email: string, productHandle: string, productTitle: string, productId: string, variantId: string}} signup
 * @returns {Promise<void>} Resolves when recorded; rejects to show the error state.
 */
async function submitSignup(signup) {
  const body = new FormData();
  body.append('form_type', 'customer');
  body.append('utf8', '✓');
  body.append('contact[email]', signup.email);
  body.append('contact[tags]', `back-in-stock,${signup.productHandle}`);

  const response = await fetch('/contact', {
    method: 'POST',
    body,
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) throw new Error(`Signup failed with status ${response.status}`);
}

class BpcNotify extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-notify-open]');
    this.panel = this.querySelector('[data-notify-panel]');
    this.input = this.querySelector('[data-notify-input]');
    this.joinButton = this.querySelector('[data-notify-join]');
    this.errorText = this.querySelector('[data-notify-error]');
    this.done = this.querySelector('[data-notify-done]');

    if (!this.openButton || !this.panel || !this.input || !this.joinButton) return;

    this.openButton.addEventListener('click', () => this.open());
    this.input.addEventListener('input', () => this.validate());
    this.joinButton.addEventListener('click', () => this.join());
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (!this.joinButton.disabled) this.join();
    });
  }

  open() {
    this.openButton.hidden = true;
    this.panel.hidden = false;
    this.input.focus();
  }

  get email() {
    return this.input.value.trim();
  }

  validate() {
    const valid = EMAIL_PATTERN.test(this.email);
    this.joinButton.disabled = !valid;
    if (valid) this.hideError();
    return valid;
  }

  showError(message) {
    if (!this.errorText) return;
    this.errorText.textContent = message;
    this.errorText.hidden = false;
  }

  hideError() {
    if (!this.errorText) return;
    this.errorText.hidden = true;
  }

  async join() {
    if (!this.validate()) return;

    this.joinButton.disabled = true;
    this.joinButton.textContent = '…';
    this.hideError();

    try {
      await submitSignup({
        email: this.email,
        productHandle: this.dataset.productHandle || '',
        productTitle: this.dataset.productTitle || '',
        productId: this.dataset.productId || '',
        variantId: this.dataset.variantId || '',
      });

      this.panel.hidden = true;
      if (this.done) this.done.hidden = false;
    } catch (error) {
      console.error('[bpc-notify] signup failed', error);
      this.joinButton.textContent = 'Join';
      this.joinButton.disabled = false;
      this.showError("That didn't go through — please try again.");
    }
  }
}

if (!customElements.get('bpc-notify')) {
  customElements.define('bpc-notify', BpcNotify);
}
